import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faCircleQuestion,
  faCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';

/**
 * Semantik eines Status-Wertes (Icon + Farbe) für `x-widget: status`.
 *
 * Bewusst frei von Angular-Abhängigkeiten und in einer eigenen Datei: an dieser Zuordnung sind
 * schon zwei Fehler aufgetreten (Teilstatus wurde grün, „Erfüllt" grau), sie braucht also einen
 * Test — und der lässt sich nur ohne Komponenten-Kontext sinnvoll fahren.
 *
 * Icons und Farben entsprechen den Berichten (report-card-view.statusIcon /
 * check-report-page), damit derselbe Status im Editor und im Bericht gleich aussieht.
 */

/**
 * Klassifiziert wird der gespeicherte ENUM-WERT (z.B. `erfuellt`), nicht das Anzeige-Label.
 * Deshalb muss die ue-Schreibweise mit abgedeckt sein: `erf[üu]llt` traf „erfüllt"/„erfullt",
 * aber nicht den tatsächlich gespeicherten Wert `erfuellt` — der landete im grauen Fallback.
 * Die Alternativen entsprechen `_SUCCESS_RE`/`_FAILURE_RE`/`_WARNING_RE` in
 * `BIC-Workflows/shared/ai_verdict.py`.
 */
const SUCCESS_RE = /(erf[üu]llt|erfuellt|success|erfolgreich|bestanden|ok|pass|true|ja|gut)/;
const FAILURE_RE = /(nicht|fail|error|fehl|false|nein|not|kein)/;
const WARNING_RE = /(warn|hinweis|teil|partial|eingeschr[äa]nkt)/;
// „unbekannt" ist ein eigenes Ergebnis, kein Fehler und kein Hinweis: graues Fragezeichen, genau
// wie im Bericht (statusIcon in report-card-view / check-report-page).
const UNKNOWN_RE = /(unbekannt|unknown|nicht relevant|nicht bewertbar|nicht anwendbar)/;

/**
 * Reihenfolge ist bedeutsam und läuft von der spezifischsten zur allgemeinsten Regel:
 * `nicht_erfuellt` und `teilweise_erfuellt` enthalten beide „erfuellt" und würden vom
 * Erfolgs-Zweig abgefangen, deshalb kommen sie davor. `unbekannt` steht ganz vorn, weil seine
 * Formulierungen („nicht relevant") das Fehler-Vokabular treffen.
 */
export function classifyStatusValue(value: any): { icon: IconDefinition; colorClass: string } {
  const v = String(value ?? '').toLowerCase();
  // UNKNOWN vor FAILURE: „nicht relevant"/„nicht bewertbar" enthalten „nicht" und wären sonst rot.
  if (UNKNOWN_RE.test(v)) {
    return {icon: faCircleQuestion, colorClass: 'text-gray-400'};
  }
  if (FAILURE_RE.test(v)) {
    return {icon: faCircleXmark, colorClass: 'text-red'};
  }
  if (WARNING_RE.test(v)) {
    return {icon: faCircleExclamation, colorClass: 'text-yellow'};
  }
  if (SUCCESS_RE.test(v)) {
    return {icon: faCircleCheck, colorClass: 'text-green'};
  }
  return {icon: faCircle, colorClass: 'text-gray-400'};
}
